/**
 * Invitation à annoncer une nouveauté, au moment de la RÉCEPTION.
 *
 * Demande de Philippe (26/09/2026) : une semaine chargée, une vingtaine
 * d'articles arrivés pour la première fois, aucun annoncé — le geste existait
 * (mise en rayon, fiche, page Nouveautés) mais rien n'y faisait penser. Le
 * serveur dit, à la validation d'une ligne, si l'article est une première
 * arrivée (« Nouveau ») ou un retour après une longue absence (« De retour ») ;
 * cet écran le demande explicitement.
 *
 * Il PROPOSE, il n'annonce rien tout seul : « Pas cette fois » reste un choix
 * d'un tap. Une fausse annonce coûte la confiance, une annonce oubliée
 * seulement une occasion.
 *
 * `proposer()` renvoie une promesse résolue quand l'opérateur a répondu : la
 * réception peut l'attendre avant de quitter l'écran (dernière ligne d'un bon),
 * sans quoi l'invitation disparaîtrait avec lui.
 */

import { useCallback, useState } from 'react';
import api from '../../services/api';
import { usePOSStore } from '../../hooks/usePOSStore';

type Genre = 'NEW' | 'BACK';

interface Invitation {
  productId: string;
  nom: string;
  genre: Genre;
  joursAbsence?: number;
  resoudre: () => void;
}

export function useInvitationNouveaute() {
  const [invitation, setInvitation] = useState<Invitation | null>(null);
  const [envoi, setEnvoi] = useState<Genre | null>(null);
  const [fait, setFait] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);

  /** À appeler avec la réponse de `approveLine` ; ne fait rien s'il n'y a rien à proposer. */
  const proposer = useCallback((resultat: any): Promise<void> => {
    const genre = resultat?.nouveaute_suggeree as Genre | undefined;
    if (!resultat?.productId || (genre !== 'NEW' && genre !== 'BACK')) return Promise.resolve();
    return new Promise<void>((resoudre) => {
      setErreur(null);
      setFait(false);
      setInvitation({
        productId: resultat.productId,
        nom: resultat.product_name || 'Ce produit',
        genre,
        joursAbsence: resultat.jours_absence,
        resoudre,
      });
    });
  }, []);

  const fermer = () => {
    invitation?.resoudre();
    setInvitation(null);
    setEnvoi(null);
    setFait(false);
    setErreur(null);
  };

  const annoncer = async (genre: Genre) => {
    if (!invitation || envoi) return;
    setEnvoi(genre);
    setErreur(null);
    try {
      const vendorId = usePOSStore.getState().vendorId;
      await api.patch(`/products/${invitation.productId}/novelty`, {
        kind: genre,
        ...(vendorId ? { vendor_id: vendorId } : {}),
      });
      setFait(true);
      setTimeout(fermer, 900);
    } catch (err: any) {
      setErreur(err?.response?.data?.message || err?.message || "L'annonce n'a pas pu être enregistrée");
      setEnvoi(null);
    }
  };

  const element = invitation ? (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" role="presentation">
      <div className="absolute inset-0 bg-black/50" />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="titre-invitation-nouveaute"
        className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl p-6"
      >
        <div className="text-4xl text-center mb-2" aria-hidden>✨</div>
        <h2 id="titre-invitation-nouveaute" className="text-xl font-bold text-gray-900 text-center mb-3">
          {invitation.genre === 'NEW' ? 'Nouveau chez nous ?' : 'De retour en rayon ?'}
        </h2>
        <p className="text-base text-gray-700 text-center mb-1">
          « <strong>{invitation.nom}</strong> »{' '}
          {invitation.genre === 'NEW'
            ? 'arrive pour la première fois.'
            : `n'avait plus été reçu depuis ${invitation.joursAbsence ?? 'longtemps'} jours.`}
        </p>
        <p className="text-sm text-gray-500 text-center mb-5">
          L'annoncer sur la boutique : badge « {invitation.genre === 'NEW' ? 'Nouveau' : 'De retour'} » et filtre
          Nouveautés, jusqu'à la prochaine ouverture au moins 5 jours plus tard.
        </p>

        {fait ? (
          <div className="py-3 rounded-xl bg-emerald-50 border border-emerald-300 text-emerald-800 font-semibold text-center">
            ✓ Annoncé sur la boutique
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            <button
              onClick={() => void annoncer(invitation.genre)}
              disabled={!!envoi}
              className="w-full min-h-[52px] rounded-xl bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 text-white text-lg font-bold disabled:bg-gray-300"
            >
              {envoi === invitation.genre
                ? 'Annonce…'
                : invitation.genre === 'NEW'
                  ? '✨ Annoncer : Nouveau'
                  : '✨ Annoncer : De retour'}
            </button>
            <div className="flex gap-2">
              <button
                onClick={() => void annoncer(invitation.genre === 'NEW' ? 'BACK' : 'NEW')}
                disabled={!!envoi}
                className="flex-1 min-h-[48px] rounded-xl border border-emerald-400 text-emerald-800 font-medium hover:bg-emerald-50 disabled:text-gray-400 disabled:border-gray-300"
              >
                {invitation.genre === 'NEW' ? 'Plutôt « De retour »' : 'Plutôt « Nouveau »'}
              </button>
              <button
                onClick={fermer}
                disabled={!!envoi}
                className="flex-1 min-h-[48px] rounded-xl border border-gray-300 text-gray-700 font-medium hover:bg-gray-50"
              >
                Pas cette fois
              </button>
            </div>
            {erreur && <p className="text-sm text-red-700 text-center mt-1">{erreur}</p>}
            <p className="text-xs text-gray-400 text-center mt-2">
              Toujours possible plus tard : Mise en rayon ou page Nouveautés.
            </p>
          </div>
        )}
      </div>
    </div>
  ) : null;

  return { proposer, element };
}
